import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useSettingsStore } from '@/stores/settingsStore'
import { useCountriesStore } from '@/stores/countriesStore'
import { getPaymentMethodsForCountry } from '@/enums/paymentMethod'
import {
  BASE_CURRENCY,
  convertFromBaseCurrency,
  formatCurrencyAmount,
  normalizeCurrencyCode,
  normalizeExchangeRate,
} from '@/utils/currency'

const STORAGE_KEY = 'countryIso'
const DEFAULT_ISO = 'CMR'
const DEFAULT_PHONE_PREFIX = '+237'
const DEFAULT_LOCALE = 'fr-CM'

export const useLocaleStore = defineStore('locale', () => {
  const settingsStore = useSettingsStore()
  const countriesStore = useCountriesStore()
  const { countries } = storeToRefs(countriesStore)

  const countryIso = ref(DEFAULT_ISO)
  const initialized = ref(false)
  const source = ref('default')

  let ipAbortController = null

  const isClient = typeof window !== 'undefined'

  const currentCountry = computed(() =>
    countries.value.find(country => country.iso3 === countryIso.value) ?? null
  )

  const hasCountries = computed(() => countries.value.length > 0)

  const currency = computed(() =>
    normalizeCurrencyCode(currentCountry.value?.currency)
  )

  const exchangeRate = computed(() =>
    normalizeExchangeRate(currentCountry.value?.exchangeRate)
  )

  const phonePrefix = computed(() =>
    currentCountry.value?.prefix ?? DEFAULT_PHONE_PREFIX
  )

  const locale = computed(() =>
    currentCountry.value?.locale ?? DEFAULT_LOCALE
  )

  const availablePaymentMethods = computed(() =>
    getPaymentMethodsForCountry({ countryIso: currentCountry.value?.iso3 })
  )

  const defaultCountryId = computed(() => {
    const raw =
      settingsStore.settings?.defaultCountry?.id ??
      settingsStore.settings?.defaultCountryId ??
      null

    const value = Number(raw)
    return Number.isFinite(value) ? value : null
  })

  const storage = {
    get() {
      if (!isClient) return null
      return window.localStorage.getItem(STORAGE_KEY)
    },
    set(iso3) {
      if (!isClient) return
      window.localStorage.setItem(STORAGE_KEY, iso3)
    },
    remove() {
      if (!isClient) return
      window.localStorage.removeItem(STORAGE_KEY)
    },
  }

  const findByIso = iso3 => {
    if (!iso3) return null
    return countries.value.find(country => country.iso3 === iso3) ?? null
  }

  const findByIso2 = iso2 => {
    if (!iso2) return null
    return countries.value.find(country => country.iso2 === iso2) ?? null
  }

  const findById = id => {
    const value = Number(id)
    if (!Number.isFinite(value)) return null
    return countries.value.find(country => country.id === value) ?? null
  }

  const getFallbackCountry = () =>
    findByIso(DEFAULT_ISO) ??
    countries.value[0] ??
    null

  const applyCountry = (
    country,
    {
      persist = false,
      source: nextSource = 'default',
      markInitialized = true,
    } = {}
  ) => {
    countryIso.value = country.iso3
    source.value = nextSource

    if (persist) {
      storage.set(country.iso3)
    }

    if (markInitialized) {
      initialized.value = true
    }

    return true
  }

  const resolveStoredCountry = () => {
    const storedIso = storage.get()
    return findByIso(storedIso)
  }

  const resolveSettingsCountry = () => {
    return findById(defaultCountryId.value)
  }

  const setCountry = (iso3) => {
    const country = findByIso(iso3)
    if (!country) return false

    return applyCountry(country, {
      persist: true,
      source: 'manual',
    })
  }

  const setFromUser = user => {
    const country =
      findById(user?.country?.id) ??
      findByIso(user?.country?.code) ??
      findByIso(user?.country?.isoCode)

    if (!country) return false

    return applyCountry(country, {
      persist: false,
      source: 'user',
    })
  }

  const restoreDefaultCountry = () => {
    if (!hasCountries.value) return false

    const fromStorage = resolveStoredCountry()
    if (fromStorage) {
      return applyCountry(fromStorage, {
        persist: false,
        source: 'storage',
      })
    }

    const fromSettings = resolveSettingsCountry()
    if (fromSettings) {
      return applyCountry(fromSettings, {
        persist: false,
        source: 'settings',
      })
    }

    const fallback = getFallbackCountry()
    if (!fallback) return false

    return applyCountry(fallback, {
      persist: false,
      source: 'default',
    })
  }

  const setFromIp = async () => {
    if (!hasCountries.value || initialized.value) return false

    if (ipAbortController) {
      ipAbortController.abort()
    }

    ipAbortController = new AbortController()

    try {
      const response = await fetch('https://ipapi.co/json/', {
        signal: ipAbortController.signal,
      })

      if (!response.ok) return false

      const data = await response.json()

      const country =
        findByIso2(data.country_code) ??
        getFallbackCountry()

      if (!country) return false

      return applyCountry(country, {
        persist: true,
        source: 'ip',
      })
    } catch (error) {
      if (error?.name === 'AbortError') {
        return false
      }

      console.warn('Géolocalisation IP échouée:', error)
      return false
    }
  }

  const init = async user => {
    if (!hasCountries.value) return false

    if (setFromUser(user)) return true
    if (restoreDefaultCountry()) return true
    return await setFromIp()
  }

  const clearStorage = () => {
    storage.remove()
  }

  const reset = () => {
    if (ipAbortController) {
      ipAbortController.abort()
    }

    ipAbortController = null
    countryIso.value = DEFAULT_ISO
    initialized.value = false
    source.value = 'default'
    clearStorage()
  }

  const convertPrice = amount =>
    convertFromBaseCurrency(amount, exchangeRate.value)

  const formatPrice = amount =>
    formatCurrencyAmount(convertPrice(amount), {
      currency: currency.value,
      locale: locale.value,
    })

  const formatBasePrice = amount =>
    formatCurrencyAmount(amount, {
      currency: BASE_CURRENCY,
      locale: DEFAULT_LOCALE,
    })

  const getPaymentMethods = ({ includeAll = false } = {}) =>
    getPaymentMethodsForCountry({
      countryIso: currentCountry.value?.iso3,
      includeAll,
    })

  return {
    countries,
    countryIso,
    currentCountry,
    hasCountries,
    initialized,
    source,
    phonePrefix,
    locale,
    currency,
    exchangeRate,
    availablePaymentMethods,
    init,
    setFromIp,
    setFromUser,
    setCountry,
    restoreDefaultCountry,
    clearStorage,
    reset,
    convertPrice,
    formatPrice,
    formatBasePrice,
    getPaymentMethods,
  }
})
